import type { PublicCountryConfig } from './types.js';
import { esPublic } from './es.public.js';
import { CL_LEGAL_ENTITY_MIN_BODY, canonicalRut } from './cl-rut.js';

/**
 * The shape of a Chilean RUT the web forms accept, in its canonical spelling:
 * seven or eight digits, a dash, the check digit (`0`–`9` or `K`). The browser
 * checks only the SHAPE; `cl.ts` does the módulo-11 check on the server. A body
 * under 1 000 000 is not a person or a company anyone registers with today.
 *
 * Exported for `cl.ts` and the tests, so the form and the server can never
 * disagree on what a complete id looks like.
 */
export const CL_RUT_SHAPE = '^[1-9][0-9]{6,7}-[0-9K]$';

// Liciora — Chile (the fourth deployment, after UZ, KZ and ES). Built on the
// Spanish edition: the same Spanish UI (es-CL copy overlay in the web), the same
// funnel, paywall and Stripe checkout. Spanish only: `locales: ['es']`,
// `defaultLocale: 'es'`, so every path is bare. Fixed decisions:
// /home/analyst/uzt-work/liciora-ops/AGENT-RULES.md §6.
export const clPublic: PublicCountryConfig = {
  code: 'cl',
  locales: ['es'],
  defaultLocale: 'es',
  // The 16 regiones live in the DB `regions` table with their Spanish names,
  // keyed by ISO 3166-2:CL (`CL-AP` … `CL-MA`, `prisma/cl-seed.ts`). Feed and
  // site are both Spanish, so `localize` is a passthrough, as on ES and KZ.
  regions: {
    localize(name, _locale) {
      return name && name.trim() ? name : null;
    },
  },
  currency: {
    code: 'CLP',
    symbol: '$',
    // format.ts's [1e12, 1e9, 1e6, 1e3] ladder, in WORDS: «$ 148,3 millones»,
    // «$ 3,2 mil millones». Not Spain's «M»: in Chile «M$» is thousands and «MM$»
    // millions, so «$ 148,3 M» read as 148 thousand (phone QA 26.10). «mil» stays.
    compactUnits: { es: ['billones', 'mil millones', 'millones', 'mil'] },
    compactUnitsOne: { es: ['billón', 'mil millones', 'millón', 'mil'] },
    // `ru` serves the API copy that is still Russian, as on ES.
    words: { es: 'pesos', ru: 'песо' },
    // Whole pesos (CLP has no cents): «1.234.567», es-CL symbols.
    numberLocale: 'es-CL',
  },
  brand: {
    name: 'Liciora',
    siteUrl: 'https://liciora.cl',
    supportEmail: 'support@liciora.cl',
    supportTelegram: null,
    publishPhone: false,
  },
  // Spanish has no grammatical case: «de Chile», «en Chile».
  countryName: {
    es: { nom: 'Chile', gen: 'Chile', prep: 'Chile' },
    ru: { nom: 'Чили', gen: 'Чили', prep: 'Чили' },
  },
  // RUT `76086428-5`: the forms keep the digits and the check digit and write the
  // canonical spelling (`format: 'rut'`); 10 characters at most.
  companyIdForm: {
    label: 'RUT',
    digits: 10,
    alphanumeric: { pattern: CL_RUT_SHAPE },
    keepsTypedName: true,
    format: 'rut',
    // A RUT body under 50 000 000 is a person (the company index's own line,
    // `cl.ts` `indexableId`): «Perfil del proveedor», and «Persona natural» for
    // the name in a lot's guest teasers. Shape only — a typo'd check digit still
    // counts by its body, so no person's name slips through on a bad DV.
    naturalPerson: {
      isPerson: (id: string) => {
        const rut = canonicalRut(id);
        return rut !== null && Number(rut.slice(0, rut.indexOf('-'))) < CL_LEGAL_ENTITY_MIN_BODY;
      },
      label: 'Persona natural',
    },
  },
  phonePlaceholder: '+56 9 1234 5678',
  // Mercado Público deadlines are Santiago wall clock (CLT/CLST — tzdb, never a
  // fixed offset: Chile has changed its DST rules several times).
  timeZone: 'America/Santiago',
  // The owner renamed the MiLicita Meta dataset to Liciora — the same pixel id.
  // Pixel and the normal PostHog load only after cookie consent, as on ES.
  analytics: { ...esPublic.analytics, metaPixelId: '1739042453832204', ga4MeasurementId: '' },
  // No Catalan/Galician/Basque in Chile; a long or opaque title still gets a short one.
  content: { readableTitles: true },
  // Email only (no bot, no WhatsApp yet); the first alert by e-mail, as on ES.
  messaging: { ...esPublic.messaging },
  // The Spanish feature set (Liciora AGENT-RULES §6: «copy the ES set»), with
  // `priceIntel` off until award history is loaded.
  // A signed-in save keeps the catalogue's budget band (phone QA 26.10).
  // Compra Ágil publishes no buyer contact, so `gov` is exempt from the
  // contact gate (p1 QA 26.10) — otherwise every Compra Ágil lot is hidden.
  features: {
    ...esPublic.features,
    priceIntel: false,
    saveSearchCarriesBudget: true,
    govContactGate: false,
  },
  // Stripe Checkout through AI CRM Inc, monthly only, like ES and KZ. Unlike ES
  // the price is quoted IVA INCLUIDO (19 %): «$ 39.990 / mes · IVA incluido».
  // The invoice may name the buyer: RUT + razón social (optional on Stripe's
  // page, `cl_tin`), no billing address.
  payments: {
    checkout: 'stripe',
    yearlyPlans: false,
    bankInvoice: false,
    ipPlan: false,
    marks: ['visa', 'mastercard', 'stripe'],
    cardPayee: esPublic.payments.cardPayee,
    vatIncludedRate: 0.19,
    // In OUR card sheet too (collapsed, optional): the details must be on the
    // Stripe Customer before the first comprobante is issued.
    checkoutCollects: { taxId: true, billingAddress: false, inCardSheet: true },
  },
};
